import React, { useEffect, useMemo, useRef, useState } from 'react';
import useHttp from '../../../hooks/http.hook';
import {
  IMAGE_LIMIT,
  normalizeProductImages,
  reindexProductImages,
} from '../../../utils/productImages';
import './ProductImagesField.css';

function buildOrderedImages(images, selectedKeys) {
  const list = normalizeProductImages(images);
  const byKey = new Map(list.map((item, i) => [String(item.url), { ...item, _key: String(item.url), _i: i }]));
  const selectedSet = new Set(selectedKeys.map(String));
  const ordered = [];

  for (const key of selectedKeys) {
    const item = byKey.get(String(key));
    if (item) ordered.push(item);
  }

  for (const item of list) {
    if (!selectedSet.has(String(item.url))) ordered.push(item);
  }

  return reindexProductImages(ordered.map(({ url }) => ({ url })));
}

export default function ProductImagesField({
  label = 'Изображения',
  images,
  onChange,
  onUploadLoadingChange,
  uploadPath = '/operator/images',
  fieldName = 'image',
  accept = 'image/*',
  maxSizeMb = 10,
  limit = IMAGE_LIMIT,
}) {
  const inputRef = useRef(null);
  const { loading, requestWithMeta } = useHttp();
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState([]);

  const list = useMemo(() => normalizeProductImages(images), [images]);
  const maxBytes = useMemo(() => maxSizeMb * 1024 * 1024, [maxSizeMb]);
  const atLimit = list.length >= limit;

  useEffect(() => {
    onUploadLoadingChange?.(loading);
    return () => {
      onUploadLoadingChange?.(false);
    };
  }, [loading, onUploadLoadingChange]);

  // Drop selection keys that no longer exist after remove/upload.
  useEffect(() => {
    const valid = new Set(list.map((item) => String(item.url)));
    setSelectedOrder((prev) => {
      const next = prev.filter((key) => valid.has(String(key)));
      return next.length === prev.length ? prev : next;
    });
  }, [list]);

  function emit(next) {
    onChange?.(reindexProductImages(next));
  }

  function getOrderNumber(url) {
    const idx = selectedOrder.findIndex((key) => String(key) === String(url));
    return idx === -1 ? null : idx + 1;
  }

  function toggleImage(url) {
    setError(null);
    const key = String(url);
    const idx = selectedOrder.findIndex((id) => String(id) === key);
    const next =
      idx === -1 ? [...selectedOrder, key] : selectedOrder.filter((id) => String(id) !== key);
    setSelectedOrder(next);
    emit(buildOrderedImages(list, next));
  }

  async function uploadFile(file) {
    setError(null);

    if (!file) return;
    if (list.length >= limit) {
      setError(`Лимит изображений: ${limit}`);
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      setError('Можно загрузить только изображение');
      return;
    }
    if (file.size > maxBytes) {
      setError(`Файл слишком большой (макс. ${maxSizeMb}MB)`);
      return;
    }

    const form = new FormData();
    form.append(fieldName, file);

    try {
      const res = await requestWithMeta(uploadPath, 'POST', form);
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить изображение');
        return;
      }

      const nextUrl = res?.data?.url;
      if (!nextUrl) {
        setError('Сервер не вернул url изображения');
        return;
      }

      if (list.length >= limit) {
        setError(`Лимит изображений: ${limit}`);
        return;
      }

      emit([...list, { url: nextUrl, index: list.length }]);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить изображение');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function removeImage(url, e) {
    e?.stopPropagation?.();
    setError(null);
    const key = String(url);
    const nextList = list.filter((item) => String(item.url) !== key);
    const nextSelected = selectedOrder.filter((id) => String(id) !== key);
    setSelectedOrder(nextSelected);
    emit(buildOrderedImages(nextList, nextSelected));
  }

  return (
    <section className="shop-create-section">
      <label className="shop-create-label">
        {label}
        <span className="product-images-limitHint">
          {' '}
          ({list.length}/{limit})
        </span>
      </label>
      <div className="shop-create-help shop-create-helpTight">
        Нажимайте на фото в нужном порядке. Первое — обложка. Повторное нажатие снимает номер.
      </div>

      <div className="product-images-list">
        {list.length === 0 ? (
          <div className="product-images-empty">Нет изображений</div>
        ) : (
          list.map((item) => {
            const orderNumber = getOrderNumber(item.url);
            const selected = orderNumber != null;
            return (
              <button
                key={item.url}
                type="button"
                className={`product-images-item${selected ? ' product-images-item--selected' : ''}`}
                onClick={() => toggleImage(item.url)}
                disabled={loading}
              >
                <div
                  className={`product-images-order${selected ? ' product-images-order--selected' : ''}`}
                >
                  {selected ? orderNumber : '·'}
                </div>
                <img className="product-images-thumb" src={item.url} alt="" />
                <span className="product-images-itemHint">
                  {selected ? `Позиция ${orderNumber}` : 'Нажмите для выбора'}
                </span>
                <span
                  className="product-images-iconBtn product-images-iconBtn--danger"
                  role="button"
                  tabIndex={0}
                  aria-label="Удалить"
                  title="Удалить"
                  onClick={(e) => removeImage(item.url, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') removeImage(item.url, e);
                  }}
                >
                  ×
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="shop-create-uploadInline" style={{ marginTop: '10px' }}>
        <input
          ref={inputRef}
          className="shop-create-fileInputHidden"
          type="file"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            uploadFile(file);
          }}
          disabled={loading || atLimit}
        />
        <button
          type="button"
          className="shop-create-buttonSecondary shop-create-uploadButton"
          onClick={() => {
            if (atLimit) {
              setError(`Лимит изображений: ${limit}`);
              return;
            }
            inputRef.current?.click?.();
          }}
          disabled={loading}
        >
          {loading ? 'Загрузка…' : atLimit ? `Лимит ${limit}` : 'Добавить фото'}
        </button>
      </div>

      {error ? (
        <div className="shop-create-help shop-create-helpStoreSettings shop-create-uploadError">
          {error}
        </div>
      ) : null}
    </section>
  );
}
